import { useDispatch } from "react-redux";

import { useDidUpdate } from "app/custom-hooks/useDidUpdate";
import { useMount } from "app/custom-hooks/useMount";
import { translate } from "app/filters/i18nFilter";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import {
    BaseActivatedPanel,
    type BaseActivatedPanelProps,
    ManualSelectionItem,
    mapLimitedListOfItemsToTopNWithCost,
    SelectionDDItem,
    type SelectionDropdown,
    type SelectionDropdownItem,
    StyledInnerContainer,
    StyledPanelIcon,
    useActionsPanelState,
} from "app/pages/sales-intelligence/common-components/table-actions-panel";
import { SelectionDropdownQuotaFooter } from "app/pages/sales-intelligence/common-components/table-actions-panel/components/SelectionDropdown/SelectionDropdownQuotaFooter/SelectionDropdownQuotaFooter";
import type TopNWithCostDropdownItem from "app/pages/sales-intelligence/common-components/table-actions-panel/models/selection-dropdown-item/TopNWithCostDropdownItem/TopNWithCostDropdownItem";
import { useColumnPickerControls } from "app/pages/sales-intelligence/hooks/useColumnPickerControls";
import { QuotaExportCostTag } from "app/pages/sales-intelligence/modules/quota/components/QuotaExportCostTag";
import { QuotaTooltipContent } from "app/pages/sales-intelligence/modules/quota/components/QuotaTooltipContent/QuotaTooltipContent";
import SelectionDDQuotaItem from "app/pages/sales-intelligence/modules/quota/components/SelectionDDQuotaItem/SelectionDDQuotaItem";
import { ExportCostTypeItem } from "app/pages/sales-intelligence/modules/quota/enums";
import { useLowQuotaBannerManagement } from "app/pages/sales-intelligence/modules/quota/hooks/useLowQuotaBannerManagement";
import type { ExportCostState } from "app/pages/sales-intelligence/modules/quota/types";
import getLimitedListOfItems from "app/pages/sales-intelligence/modules/quota/utils/getLimitedListOfItems";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import { MAX_LEADS_EXPORT_SELECTION_TOP_N_VALUE } from "app/pages/sales-intelligence/sub-modules/company-search/constants/websites-table";

import { setIsExcelExportActive } from "../../store/action-creators";

import {
    StyledExportQuotaTagContainer,
    StyledInfoMessage,
    StyledSelectionDropdown,
    StyledSubmitButton,
    StyledTitle,
} from "./styles";

type WebsitesExcelExportPanelViewProps = BaseActivatedPanelProps & {
    isDisabled: boolean;
    isLoading: boolean;
    numberOfSelectedItems: number;
    numberOfRemainingExports: number;
    numberOfResults: number;
    numberOfTableColumns?: number;
    selectionDropdown: SelectionDropdown;
    exportType?: ExportCostTypeItem;
    exportCostState: ExportCostState;
    onSelectionChange(item: Readonly<SelectionDropdownItem>): void;
    onSubmit(activeSelectionItem: Readonly<SelectionDropdownItem>): void;
};

const WebsitesExcelExportPanelView = (props: WebsitesExcelExportPanelViewProps) => {
    const dispatch = useDispatch();
    const {
        isDisabled,
        isLoading,
        numberOfRemainingExports,
        numberOfResults,
        numberOfSelectedItems,
        numberOfTableColumns = 0,
        selectionDropdown,
        exportType = ExportCostTypeItem.Excel,
        exportCostState: { quota, pricePerRecord, isLowQuota: checkIsLowCredits, getExportCost, canAfford },
        onSelectionChange,
        onSubmit,
    } = props;

    const { openWithDataCost } = useColumnPickerControls();

    const handleMappingLimitedListOfItems = (listOfItems: readonly number[]): readonly TopNWithCostDropdownItem[] => {
        const possibleTopNValues = getLimitedListOfItems(
            listOfItems,
            quota.data.remaining,
            pricePerRecord,
            numberOfResults,
            MAX_LEADS_EXPORT_SELECTION_TOP_N_VALUE,
        );

        return mapLimitedListOfItemsToTopNWithCost(possibleTopNValues);
    };

    const state = useActionsPanelState(selectionDropdown, {
        numberOfResults,
        numberOfSelectedItems,
        onSelectionChange,
        limit: numberOfRemainingExports,
        mapLimitedListOfItems: salesSettingsHelper.isDataCreditsEnabled ? handleMappingLimitedListOfItems : undefined,
    });

    const isLowCredits = checkIsLowCredits(state.activeSelectionItem.value);
    const isSubmitDisabled = isDisabled || state.isSelectionEmpty || isLowCredits;

    const { lowCreditsBannerConfigs } = useLowQuotaBannerManagement();

    useMount(() => {
        dispatch(setIsExcelExportActive(true));
        return () => {
            dispatch(setIsExcelExportActive(false));
        };
    });

    useDidUpdate(() => {
        if (!isLoading) {
            onSelectionChange(state.activeSelectionItem);
        }
    }, [isLoading]);

    function handleSubmit() {
        onSubmit(state.activeSelectionItem);
    }

    return (
        <BaseActivatedPanel onDeactivate={props.onDeactivate}>
            <StyledInnerContainer>
                <StyledPanelIcon
                    iconName="excel"
                    size="xs"
                />
                <I18n
                    data-automation="actions-panel.export-label"
                    element={StyledTitle}
                    translatable={{ key: "si.multi_selector.excel.title" }}
                />
                <StyledSelectionDropdown
                    buttonText={state.activeSelectionItem.getSelectedTranslation()}
                    isLoading={isLoading}
                    isDisabled={isDisabled}
                    isHideFooterDivider
                    footer={
                        <SelectionDropdownQuotaFooter
                            totalQuota={quota.data.total}
                            remainingQuota={quota.data.remaining}
                            pricePerRecord={pricePerRecord}
                            maxOptionValue={state.dropdownItems[state.dropdownItems.length - 1]?.value}
                        />
                    }
                    onSelect={state.setActiveSelectionItem}
                >
                    {state.dropdownItems
                        .map((item) => (
                            <SelectionDDQuotaItem
                                key={item.id}
                                item={item}
                                isSelected={state.activeSelectionItem.id === item.id}
                                getOperationCost={getExportCost}
                                canAffordOperation={canAfford}
                            />
                        ))
                        .concat(
                            <SelectionDDItem
                                item={state.manualSelectionItem}
                                key={ManualSelectionItem.ID}
                                isSelected={state.activeSelectionItem.id === ManualSelectionItem.ID}
                            />,
                        )}
                </StyledSelectionDropdown>
                {state.activeSelectionItem ? (
                    <StyledExportQuotaTagContainer>
                        <QuotaExportCostTag
                            quota={getExportCost(state.activeSelectionItem.value)}
                            pricePerRecord={pricePerRecord}
                            isLowQuota={isLowCredits}
                            withTooltip={!isLowCredits}
                            exportType={exportType}
                            onExportCostChange={() => {
                                openWithDataCost(quota.data.remaining, getExportCost(state.activeSelectionItem.value));
                                props.onDeactivate();
                            }}
                        />
                    </StyledExportQuotaTagContainer>
                ) : null}

                <StyledSubmitButton
                    isDisabled={isSubmitDisabled}
                    isLoading={isLoading}
                    text={translate("si.common.button.export")}
                    tooltipContent={
                        isSubmitDisabled && isLowCredits ? (
                            <QuotaTooltipContent
                                title={translate(lowCreditsBannerConfigs.titleKey)}
                                description={translate(lowCreditsBannerConfigs.descriptionKey)}
                                buttonText={
                                    lowCreditsBannerConfigs.buttonTextKey
                                        ? translate(lowCreditsBannerConfigs.buttonTextKey)
                                        : undefined
                                }
                                onClick={lowCreditsBannerConfigs.onClick}
                            />
                        ) : (
                            translate("si.multi_selector.tooltip.excel.unselected_domains")
                        )
                    }
                    onClick={handleSubmit}
                />
                {numberOfTableColumns > 0 ? (
                    <StyledInfoMessage>
                        {translate("si.multi_selector.excel.columns_info", {
                            numberOfColumns: numberOfTableColumns,
                        })}
                    </StyledInfoMessage>
                ) : null}
            </StyledInnerContainer>
        </BaseActivatedPanel>
    );
};

export default WebsitesExcelExportPanelView;
